/**
 * Moodle Import Content Component
 * Reusable content for Moodle import functionality
 * Used within Fast Mark tabs
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Stack, FormControl, InputLabel, Select, MenuItem, Card, CardContent,
  Divider, List, ListItem, ListItemText, ListItemButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  LinearProgress, FormControlLabel, Radio, RadioGroup, FormLabel,
  Autocomplete, Link as MuiLink
} from '@mui/material';
import axios from 'axios';
import { useAuthenticatedRequest } from '../../hooks/useAuthenticatedRequest';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import GradingIcon from '@mui/icons-material/Grading';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import InputAdornment from '@mui/material/InputAdornment';

interface MoodleLearner {
  firstname: string;
  lastname: string;
  email: string;
  full_name: string;
  status: string;
  started: string;
  completed: string;
  duration: string;
  grade: string;
  responses: { [key: string]: string };
  response_count: number;
  matched_learner_id: number | null;
  matched_learner_name: string | null;
}

interface Assignment {
  id: number;
  title: string;
  rubric_id: number | null;
}

interface Rubric {
  id: number;
  name: string;
  description: string;
}

interface MarkingResult {
  success: boolean;
  submission_id: number;
  learner_name: string;
  ai_score: string;
  ai_feedback: any;
  error?: string;
}

const MoodleImportContent: React.FC = () => {
  const { request, isReady } = useAuthenticatedRequest();

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [uploadMode, setUploadMode] = useState<'file' | 'text'>('file');
  const [fileType, setFileType] = useState<'json' | 'html'>('json');
  const [uploading, setUploading] = useState(false);

  // Learner data
  const [learners, setLearners] = useState<MoodleLearner[]>([]);
  const [selectedLearner, setSelectedLearner] = useState<MoodleLearner | null>(null);
  const [allLearners, setAllLearners] = useState<any[]>([]);
  const [manualMatches, setManualMatches] = useState<{ [key: string]: number | null }>({});
  const [markedLearners, setMarkedLearners] = useState<Set<string>>(new Set());

  // Search/Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'matched' | 'unmatched' | 'marked' | 'unmarked'>('all');

  // Assignment/Rubric selection
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [selectedRubric, setSelectedRubric] = useState<Rubric | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  // Marking state
  const [marking, setMarking] = useState(false);
  const [markingResult, setMarkingResult] = useState<MarkingResult | null>(null);
  const [showResultDialog, setShowResultDialog] = useState(false);

  // Submission action dialog
  const [showSubmissionActionDialog, setShowSubmissionActionDialog] = useState(false);
  const [submissionAction, setSubmissionAction] = useState<'create_only' | 'create_and_mark'>('create_only');

  // End-of-run reconciliation summary
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [summaryDismissed, setSummaryDismissed] = useState(false);

  // Bulk submission state — process all matched, not-yet-processed learners in one run.
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number; current: string }>({ done: 0, total: 0, current: '' });
  const [bulkResults, setBulkResults] = useState<{ name: string; ok: boolean; detail: string }[]>([]);
  const [showBulkResultDialog, setShowBulkResultDialog] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Add to Class state
  const [classes, setClasses] = useState<any[]>([]);
  const [showAddToClassDialog, setShowAddToClassDialog] = useState(false);
  const [learnerToAdd, setLearnerToAdd] = useState<MoodleLearner | null>(null);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [lastSelectedClass, setLastSelectedClass] = useState<number | null>(null); // Remember last selected class
  const [addingToClass, setAddingToClass] = useState(false);
  const [actionAfterAdding, setActionAfterAdding] = useState<'mark_now' | 'create_submission' | 'add_only'>('mark_now');

  // Load assignments, rubrics, and classes on mount
  useEffect(() => {
    if (!isReady) return;

    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [assignmentsRes, rubricsRes, classesRes, learnersRes] = await Promise.all([
          request(() => axios.get('/api/assignments/')),
          request(() => axios.get('/api/rubrics/')),
          request(() => axios.get('/api/school-classes/')),
          request(() => axios.get('/api/learners/'))
        ]);
        setAssignments(assignmentsRes.data.results || []);
        setRubrics(rubricsRes.data.results || []);
        setClasses(classesRes.data.results || []);
        setAllLearners(learnersRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load assignments and rubrics');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // Auto-select rubric when assignment changes
  useEffect(() => {
    if (selectedAssignment?.rubric_id) {
      const rubric = rubrics.find(r => r.id === selectedAssignment.rubric_id);
      if (rubric) {
        setSelectedRubric(rubric);
      }
    }
  }, [selectedAssignment, rubrics]);

  // --- Reconciliation counts (derived) ---
  // Unmatched = no Caliper learner linked. These will NOT be imported.
  const unmatchedLearners = learners.filter(l => !l.matched_learner_id);
  // Matched-but-not-yet-processed = linked, but no submission created/marked yet.
  const matchedUnprocessed = learners.filter(
    l => l.matched_learner_id && !markedLearners.has(`${l.email}_${l.full_name}`)
  );

  // Surface an end-of-run summary once every MATCHED learner has been processed
  // but unmatched learners remain — so the teacher can't silently leave them behind.
  useEffect(() => {
    if (
      learners.length > 0 &&
      matchedUnprocessed.length === 0 &&
      unmatchedLearners.length > 0 &&
      markedLearners.size > 0 &&
      !summaryDismissed
    ) {
      setShowSummaryDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markedLearners, learners, summaryDismissed]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleParseJson = async () => {
    setError(null);
    setLearners([]);
    // Fresh import — reset reconciliation summary state.
    setMarkedLearners(new Set());
    setShowSummaryDialog(false);
    setSummaryDismissed(false);

    if (uploadMode === 'file' && !selectedFile) {
      setError(`Please select a ${fileType.toUpperCase()} file`);
      return;
    }

    if (uploadMode === 'text' && !jsonText.trim()) {
      setError('Please paste JSON data');
      return;
    }

    setUploading(true);

    try {
      let response;

      if (uploadMode === 'file') {
        const formData = new FormData();
        formData.append('file', selectedFile!);
        formData.append('file_type', fileType);

        const endpoint = fileType === 'html' ? '/api/moodle/parse-html/' : '/api/moodle/parse/';
        response = await request(() => axios.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }));
      } else {
        response = await request(() => axios.post('/api/moodle/parse/', {
          json_data: jsonText
        }));
      }

      if (response.data.success) {
        setLearners(response.data.learners);
        if (response.data.learners.length === 0) {
          setError('No learners found in the data');
        }
      } else {
        setError(response.data.error || `Failed to parse ${fileType.toUpperCase()}`);
      }
    } catch (err: any) {
      console.error('Error parsing Moodle data:', err);
      setError(err.response?.data?.error || `Failed to parse Moodle ${fileType.toUpperCase()}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSelectLearner = (learner: MoodleLearner) => {
    setSelectedLearner(learner);
    setMarkingResult(null);
    setError(null);
  };

  const handleManualMatch = (moodleLearner: MoodleLearner, caliperLearnerId: number | null) => {
    const learnerKey = `${moodleLearner.email}_${moodleLearner.full_name}`;

    setManualMatches(prev => ({
      ...prev,
      [learnerKey]: caliperLearnerId
    }));

    setLearners(prevLearners =>
      prevLearners.map(l => {
        if (l.email === moodleLearner.email && l.full_name === moodleLearner.full_name) {
          const matchedLearner = allLearners.find(al => al.id === caliperLearnerId);
          return {
            ...l,
            matched_learner_id: caliperLearnerId,
            matched_learner_name: matchedLearner ? `${matchedLearner.first_name} ${matchedLearner.last_name}` : null
          };
        }
        return l;
      })
    );

    if (selectedLearner && selectedLearner.email === moodleLearner.email && selectedLearner.full_name === moodleLearner.full_name) {
      const matchedLearner = allLearners.find(al => al.id === caliperLearnerId);
      setSelectedLearner({
        ...selectedLearner,
        matched_learner_id: caliperLearnerId,
        matched_learner_name: matchedLearner ? `${matchedLearner.first_name} ${matchedLearner.last_name}` : null
      });
    }
  };

  const handleMarkLearner = async () => {
    if (!selectedLearner) {
      setError('Please select a learner');
      return;
    }

    const learnerId = selectedLearner.matched_learner_id;
    if (!learnerId) {
      setError('This learner is not matched to any Caliper learner. Please select a learner manually or create a new one.');
      return;
    }

    if (!selectedAssignment) {
      setError('Please select an assignment');
      return;
    }

    // Always default to 'create_only' - let user choose in dialog
    setSubmissionAction('create_only');

    setShowSubmissionActionDialog(true);
  };

  const handleSubmissionAction = async () => {
    if (!selectedLearner || !selectedLearner.matched_learner_id) {
      setError('Please select a learner');
      return;
    }

    setShowSubmissionActionDialog(false);

    if (submissionAction === 'create_and_mark') {
      await markLearner(selectedLearner, selectedLearner.matched_learner_id);
    } else {
      await createSubmission(selectedLearner, selectedLearner.matched_learner_id);
    }
  };

  const handleCloseResultDialog = () => {
    setShowResultDialog(false);
  };

  const handleMarkNext = () => {
    setShowResultDialog(false);
    setMarkingResult(null);

    const currentIndex = learners.findIndex(l => l === selectedLearner);
    if (currentIndex < learners.length - 1) {
      setSelectedLearner(learners[currentIndex + 1]);
    }
  };

  const handleOpenAddToClass = (learner: MoodleLearner) => {
    setLearnerToAdd(learner);
    // Pre-select the last used class if available
    setSelectedClass(lastSelectedClass);

    if (selectedAssignment && selectedRubric) {
      setActionAfterAdding('mark_now');
    } else if (selectedAssignment) {
      setActionAfterAdding('create_submission');
    } else {
      setActionAfterAdding('add_only');
    }

    setShowAddToClassDialog(true);
  };

  const handleAddToClass = async () => {
    if (!learnerToAdd || !selectedClass) {
      setError('Please select a class');
      return;
    }

    if (actionAfterAdding === 'mark_now') {
      if (!selectedAssignment || !selectedRubric) {
        setError('Please select an assignment and rubric before marking');
        return;
      }
    } else if (actionAfterAdding === 'create_submission') {
      if (!selectedAssignment) {
        setError('Please select an assignment before creating submission');
        return;
      }
    }

    setAddingToClass(true);
    setError(null);

    try {
      const response = await request(() => axios.post('/api/moodle/create-learner/', {
        firstname: learnerToAdd.firstname,
        lastname: learnerToAdd.lastname,
        email: learnerToAdd.email,
        class_id: selectedClass
      }));

      const updatedLearner = {
        ...learnerToAdd,
        matched_learner_id: response.data.learner_id,
        matched_learner_name: response.data.learner_name
      };

      const updatedLearners = learners.map(l =>
        l === learnerToAdd ? updatedLearner : l
      );
      setLearners(updatedLearners);

      if (selectedLearner === learnerToAdd) {
        setSelectedLearner(updatedLearner);
      }

      // Remember the selected class for next time
      setLastSelectedClass(selectedClass);

      setShowAddToClassDialog(false);
      setLearnerToAdd(null);
      // Don't reset selectedClass here - it's already saved to lastSelectedClass

      if (actionAfterAdding === 'mark_now' && selectedAssignment && selectedRubric) {
        setSelectedLearner(updatedLearner);
        setTimeout(async () => {
          await markLearner(updatedLearner, response.data.learner_id);
        }, 500);
      } else if (actionAfterAdding === 'create_submission' && selectedAssignment) {
        setSelectedLearner(updatedLearner);
        setTimeout(async () => {
          await createSubmission(updatedLearner, response.data.learner_id);
        }, 500);
      }
    } catch (err: any) {
      console.error('Error adding learner to class:', err);
      setError(err.response?.data?.error || 'Failed to add learner to class');
    } finally {
      setAddingToClass(false);
    }
  };

  const markLearner = async (learner: MoodleLearner, learnerId: number) => {
    if (!selectedAssignment || !selectedRubric) {
      setError('Please select an assignment and rubric');
      return;
    }

    setMarking(true);
    setError(null);

    try {
      const response = await request(() => axios.post('/api/moodle/mark-learner/', {
        learner_id: learnerId,
        assignment_id: selectedAssignment.id,
        rubric_id: selectedRubric.id,
        responses: learner.responses
      }));

      setMarkingResult(response.data);
      setShowResultDialog(true);

      const learnerKey = `${learner.email}_${learner.full_name}`;
      setMarkedLearners(prev => new Set(prev).add(learnerKey));
    } catch (err: any) {
      console.error('Error marking learner:', err);
      setError(err.response?.data?.error || 'Failed to mark learner');
    } finally {
      setMarking(false);
    }
  };

  const createSubmission = async (learner: MoodleLearner, learnerId: number) => {
    if (!selectedAssignment) {
      setError('Please select an assignment');
      return;
    }

    setMarking(true);
    setError(null);

    try {
      const response = await request(() => axios.post('/api/moodle/create-submission/', {
        learner_id: learnerId,
        assignment_id: selectedAssignment.id,
        rubric_id: selectedRubric?.id || null,
        responses: learner.responses
      }));

      setMarkingResult({
        success: true,
        submission_id: response.data.submission_id,
        learner_name: response.data.learner_name,
        ai_score: 'Not yet marked',
        ai_feedback: { message: 'Submission created successfully. Teacher can mark this later in the Submissions workflow.' }
      });
      setShowResultDialog(true);

      const learnerKey = `${learner.email}_${learner.full_name}`;
      setMarkedLearners(prev => new Set(prev).add(learnerKey));
    } catch (err: any) {
      console.error('Error creating submission:', err);
      setError(err.response?.data?.error || 'Failed to create submission');
    } finally {
      setMarking(false);
    }
  };

  // Matched learners that have NOT yet been processed (no submission created/marked).
  // This is the working set for bulk actions — re-running won't re-submit done ones.
  const bulkEligible = learners.filter(
    l => l.matched_learner_id && !markedLearners.has(`${l.email}_${l.full_name}`)
  );

  // Process every eligible learner sequentially through one endpoint. Sequential
  // (not parallel) keeps load gentle and ordering predictable — important for AI
  // marking, which is slow and rate-sensitive. Each learner's outcome is collected
  // so the teacher gets one summary instead of N dialogs.
  const runBulk = async (action: 'create_only' | 'create_and_mark') => {
    if (!selectedAssignment) {
      setError('Please select an assignment first');
      return;
    }
    if (action === 'create_and_mark' && !selectedRubric) {
      setError('Select a rubric before bulk marking');
      return;
    }
    const queue = bulkEligible;
    if (queue.length === 0) {
      setError('No matched, unprocessed learners to submit');
      return;
    }

    setError(null);
    setBulkResults([]);
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: queue.length, current: '' });

    const results: { name: string; ok: boolean; detail: string }[] = [];
    const endpoint = action === 'create_and_mark' ? '/api/moodle/mark-learner/' : '/api/moodle/create-submission/';

    for (let i = 0; i < queue.length; i++) {
      const learner = queue[i];
      setBulkProgress({ done: i, total: queue.length, current: learner.full_name });
      try {
        const payload: any = {
          learner_id: learner.matched_learner_id,
          assignment_id: selectedAssignment.id,
          rubric_id: selectedRubric?.id || null,
          responses: learner.responses,
        };
        const resp = await request(() => axios.post(endpoint, payload));
        const detail = action === 'create_and_mark'
          ? `Marked: ${resp.data.ai_score ?? '—'}`
          : `Submission #${resp.data.submission_id ?? '—'} created`;
        results.push({ name: learner.full_name, ok: true, detail });
        // Mark as processed so it leaves the eligible set and shows the "Marked" chip.
        setMarkedLearners(prev => new Set(prev).add(`${learner.email}_${learner.full_name}`));
      } catch (err: any) {
        results.push({
          name: learner.full_name,
          ok: false,
          detail: err.response?.data?.error || 'Failed',
        });
      }
    }

    setBulkProgress({ done: queue.length, total: queue.length, current: '' });
    setBulkResults(results);
    setBulkRunning(false);
    setShowBulkResultDialog(true);
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Step 1: Upload Moodle Data */}
      {learners.length === 0 && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Step 1: Upload Moodle Data</Typography>

          <Stack spacing={2} sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Upload Method</InputLabel>
              <Select
                value={uploadMode}
                onChange={(e) => setUploadMode(e.target.value as 'file' | 'text')}
                label="Upload Method"
              >
                <MenuItem value="file">Upload File</MenuItem>
                <MenuItem value="text">Paste JSON Text</MenuItem>
              </Select>
            </FormControl>

            {uploadMode === 'file' && (
              <FormControl fullWidth>
                <InputLabel>File Type</InputLabel>
                <Select
                  value={fileType}
                  onChange={(e) => {
                    setFileType(e.target.value as 'json' | 'html');
                    setSelectedFile(null);
                  }}
                  label="File Type"
                >
                  <MenuItem value="json">JSON Export</MenuItem>
                  <MenuItem value="html">HTML Export</MenuItem>
                </Select>
              </FormControl>
            )}

            {uploadMode === 'file' ? (
              <Box>
                <input
                  accept={fileType === 'json' ? '.json' : '.html,.htm'}
                  style={{ display: 'none' }}
                  id="moodle-file-upload-tab"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="moodle-file-upload-tab">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    {selectedFile ? selectedFile.name : `Choose ${fileType.toUpperCase()} File`}
                  </Button>
                </label>
              </Box>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Paste JSON Data"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder='[{"firstname":"John","lastname":"Doe","emailaddress":"john@example.com",...}]'
              />
            )}

            <Button
              variant="contained"
              onClick={handleParseJson}
              disabled={uploading || (uploadMode === 'file' && !selectedFile) || (uploadMode === 'text' && !jsonText.trim())}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Parsing...' : 'Parse Moodle Data'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Step 2: Select Assignment & Rubric */}
      {learners.length > 0 && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Step 2: Select Assignment & Rubric</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Assignment</InputLabel>
              <Select
                value={selectedAssignment?.id || ''}
                onChange={(e) => {
                  const assignment = assignments.find(a => a.id === e.target.value);
                  setSelectedAssignment(assignment || null);
                }}
                label="Assignment"
                disabled={loadingData}
              >
                <MenuItem value=""><em>Select assignment...</em></MenuItem>
                {assignments.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Rubric</InputLabel>
              <Select
                value={selectedRubric?.id || ''}
                onChange={(e) => {
                  const rubric = rubrics.find(r => r.id === e.target.value);
                  setSelectedRubric(rubric || null);
                }}
                label="Rubric"
                disabled={loadingData}
              >
                <MenuItem value=""><em>Select rubric...</em></MenuItem>
                {rubrics.map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.description || r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      )}

      {/* Step 3: Learner List & Selection */}
      {learners.length > 0 && (() => {
        // Filter learners based on search and status
        const filteredLearners = learners.filter(learner => {
          const learnerKey = `${learner.email}_${learner.full_name}`;
          const isMarked = markedLearners.has(learnerKey);

          // Search filter
          const matchesSearch = searchQuery.trim() === '' ||
            learner.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            learner.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            learner.firstname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            learner.lastname.toLowerCase().includes(searchQuery.toLowerCase());

          // Status filter
          let matchesStatus = true;
          if (filterStatus === 'matched') {
            matchesStatus = learner.matched_learner_id !== null;
          } else if (filterStatus === 'unmatched') {
            matchesStatus = learner.matched_learner_id === null;
          } else if (filterStatus === 'marked') {
            matchesStatus = isMarked;
          } else if (filterStatus === 'unmarked') {
            matchesStatus = !isMarked;
          }

          return matchesSearch && matchesStatus;
        });

        return (
          <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
            {/* Learner List */}
            <Paper sx={{ flex: 1, p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Step 3: Select Learner
                </Typography>
                {markedLearners.size > 0 && (
                  <Chip
                    icon={<AssignmentTurnedInIcon />}
                    label={`${markedLearners.size} of ${learners.length} Marked`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Search and Filter Controls */}
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  <FilterListIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="textSecondary">Filter:</Typography>
                  <Chip
                    label="All"
                    size="small"
                    color={filterStatus === 'all' ? 'primary' : 'default'}
                    onClick={() => setFilterStatus('all')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="Matched"
                    size="small"
                    color={filterStatus === 'matched' ? 'success' : 'default'}
                    onClick={() => setFilterStatus('matched')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="Unmatched"
                    size="small"
                    color={filterStatus === 'unmatched' ? 'warning' : 'default'}
                    onClick={() => setFilterStatus('unmatched')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="Marked"
                    size="small"
                    color={filterStatus === 'marked' ? 'primary' : 'default'}
                    onClick={() => setFilterStatus('marked')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="Unmarked"
                    size="small"
                    color={filterStatus === 'unmarked' ? 'default' : 'default'}
                    onClick={() => setFilterStatus('unmarked')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Stack>

                <Typography variant="caption" color="textSecondary">
                  Showing {filteredLearners.length} of {learners.length} learners
                </Typography>
              </Stack>

              {unmatchedLearners.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                  <strong>{unmatchedLearners.length} of {learners.length} learners are not matched and will NOT be imported.</strong>{' '}
                  Link each to an existing learner, or use <em>Add to Class</em>, before you finish.
                  <MuiLink
                    component="button"
                    variant="body2"
                    onClick={() => setFilterStatus('unmatched')}
                    sx={{ ml: 1 }}
                  >
                    Show only unmatched
                  </MuiLink>
                </Alert>
              )}

              {/* Bulk actions — create/mark all matched, unprocessed learners at once */}
              {bulkEligible.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2">
                        Bulk actions — {bulkEligible.length} matched learner{bulkEligible.length === 1 ? '' : 's'} ready
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Processes all matched learners not yet submitted. Unmatched ones are skipped.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={bulkRunning ? <CircularProgress size={18} color="inherit" /> : <AssignmentTurnedInIcon />}
                        disabled={bulkRunning || !selectedAssignment}
                        onClick={() => runBulk('create_only')}
                      >
                        Create submissions ({bulkEligible.length})
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<GradingIcon />}
                        disabled={bulkRunning || !selectedAssignment || !selectedRubric}
                        onClick={() => runBulk('create_and_mark')}
                      >
                        Create &amp; mark with AI ({bulkEligible.length})
                      </Button>
                    </Stack>
                  </Stack>
                  {bulkRunning && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {bulkProgress.done} / {bulkProgress.total} done
                        {bulkProgress.current ? ` — processing ${bulkProgress.current}…` : ''}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}

              {!selectedAssignment && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Tip:</strong> Select an assignment in Step 2 above before adding learners.
                </Alert>
              )}

              {filteredLearners.length === 0 ? (
                <Alert severity="info">
                  No learners match your search or filter criteria.
                </Alert>
              ) : (
                <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                  {filteredLearners.map((learner, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    selected={selectedLearner === learner}
                    onClick={() => handleSelectLearner(learner)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <PersonIcon fontSize="small" />
                          {learner.full_name}
                          {(() => {
                            const learnerKey = `${learner.email}_${learner.full_name}`;
                            const isMarked = markedLearners.has(learnerKey);

                            return (
                              <>
                                {learner.matched_learner_id ? (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Matched"
                                    color="success"
                                    size="small"
                                  />
                                ) : (
                                  <Box display="flex" alignItems="center" gap={1} sx={{ ml: 1 }} flexWrap="wrap">
                                    <Chip
                                      icon={<WarningIcon />}
                                      label="Not Matched"
                                      color="warning"
                                      size="small"
                                    />
                                    <Autocomplete
                                      size="small"
                                      options={allLearners}
                                      getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                                      onChange={(e, value) => {
                                        e.stopPropagation();
                                        handleManualMatch(learner, value ? value.id : null);
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Select Existing Learner"
                                          size="small"
                                        />
                                      )}
                                      sx={{ minWidth: 250 }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <Typography variant="caption" color="text.secondary">or</Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAddToClass(learner);
                                      }}
                                    >
                                      Add to Class
                                    </Button>
                                  </Box>
                                )}
                                {isMarked && (
                                  <Chip
                                    icon={<AssignmentTurnedInIcon />}
                                    label="Marked"
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      }
                      secondary={
                        <>
                          {learner.email} • {learner.response_count} responses • {learner.status}
                          {learner.matched_learner_name && (
                            <span> • Matched: {learner.matched_learner_name}</span>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
                </List>
              )}
            </Paper>

            {/* Learner Details & Submission Button */}
            {selectedLearner && (
            <Paper sx={{ flex: 1, p: 3 }}>
              <Typography variant="h6" gutterBottom>Step 4: Create Submission</Typography>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>{selectedLearner.full_name}</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Email: {selectedLearner.email}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Status: {selectedLearner.status}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Duration: {selectedLearner.duration}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Responses: {selectedLearner.response_count} questions
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Responses Preview:
                  </Typography>
                  {Object.entries(selectedLearner.responses).slice(0, 2).map(([qNum, answer]) => (
                    <Box key={qNum} sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary">
                        Question {qNum}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {answer.substring(0, 100)}{answer.length > 100 ? '...' : ''}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={marking ? <CircularProgress size={20} color="inherit" /> : <GradingIcon />}
                onClick={handleMarkLearner}
                disabled={marking || !selectedAssignment || !selectedLearner.matched_learner_id}
              >
                {marking ? 'Processing...' : 'Create Submission'}
              </Button>

              {!selectedLearner.matched_learner_id && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This learner is not matched to any Caliper learner. Please create the learner in the system first.
                </Alert>
              )}

              {!selectedAssignment && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please select an assignment in Step 2 to create a submission.
                </Alert>
              )}
            </Paper>
            )}
          </Box>
        );
      })()}

      {/* Results Dialog */}
      <Dialog open={showResultDialog} onClose={handleCloseResultDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          AI Marking Results - {markingResult?.learner_name}
        </DialogTitle>
        <DialogContent>
          {markingResult?.success ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                {submissionAction === 'create_and_mark' ? 'Marking completed successfully!' : 'Submission created successfully!'}
              </Alert>

              <Typography variant="h6" gutterBottom>
                Score: {markingResult.ai_score}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Feedback:
              </Typography>

              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                  {JSON.stringify(markingResult.ai_feedback, null, 2)}
                </pre>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Submission ID: {markingResult.submission_id} - Review this in the submissions list
              </Alert>
            </>
          ) : (
            <Alert severity="error">
              {markingResult?.error || 'Operation failed'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultDialog}>Close</Button>
          <Button onClick={handleMarkNext} variant="contained" color="primary">
            Mark Next Learner
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Action Dialog */}
      <Dialog open={showSubmissionActionDialog} onClose={() => setShowSubmissionActionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Submission Action</DialogTitle>
        <DialogContent>
          {selectedLearner && (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>{selectedLearner.full_name}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {selectedLearner.response_count} responses for {selectedAssignment?.title}
              </Typography>

              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography variant="body2" fontWeight="bold">
                    What would you like to do with this submission?
                  </Typography>
                </FormLabel>
                <RadioGroup
                  value={submissionAction}
                  onChange={(e) => setSubmissionAction(e.target.value as 'create_only' | 'create_and_mark')}
                >
                  <FormControlLabel
                    value="create_only"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Create submission only
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Save responses for later marking
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="create_and_mark"
                    control={<Radio />}
                    disabled={!selectedRubric}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Create submission and mark with AI
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Create and mark immediately
                        </Typography>
                        {!selectedRubric && (
                          <Typography variant="caption" color="error" display="block">
                            Requires rubric in Step 2
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmissionActionDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmissionAction} variant="contained" color="primary">
            {submissionAction === 'create_and_mark' ? 'Create & Mark' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Class Dialog */}
      <Dialog open={showAddToClassDialog} onClose={() => setShowAddToClassDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Learner to Class</DialogTitle>
        <DialogContent>
          {learnerToAdd && (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>{learnerToAdd.full_name}</strong> ({learnerToAdd.email})
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                This learner will be created in Caliper and enrolled in the selected class.
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Class</InputLabel>
                <Select
                  value={selectedClass || ''}
                  onChange={(e) => setSelectedClass(Number(e.target.value))}
                  label="Select Class"
                  disabled={addingToClass}
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name} - Grade {cls.grade_level} {cls.subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography variant="body2" fontWeight="bold">
                    What to do after adding?
                  </Typography>
                </FormLabel>
                <RadioGroup
                  value={actionAfterAdding}
                  onChange={(e) => setActionAfterAdding(e.target.value as any)}
                >
                  <FormControlLabel
                    value="mark_now"
                    control={<Radio />}
                    disabled={!selectedAssignment || !selectedRubric}
                    label={
                      <Box>
                        <Typography variant="body2">Mark immediately with AI</Typography>
                        {(!selectedAssignment || !selectedRubric) && (
                          <Typography variant="caption" color="error">Requires assignment & rubric</Typography>
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="create_submission"
                    control={<Radio />}
                    disabled={!selectedAssignment}
                    label={
                      <Box>
                        <Typography variant="body2">Create submission for later</Typography>
                        {!selectedAssignment && (
                          <Typography variant="caption" color="error">Requires assignment</Typography>
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="add_only"
                    control={<Radio />}
                    label="Add to class only"
                  />
                </RadioGroup>
              </FormControl>

              {addingToClass && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddToClassDialog(false)} disabled={addingToClass}>Cancel</Button>
          <Button onClick={handleAddToClass} variant="contained" disabled={!selectedClass || addingToClass}>
            {actionAfterAdding === 'mark_now' && 'Add & Mark'}
            {actionAfterAdding === 'create_submission' && 'Add & Create'}
            {actionAfterAdding === 'add_only' && 'Add to Class'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* End-of-Run Reconciliation Summary */}
      <Dialog
        open={showSummaryDialog}
        onClose={() => { setShowSummaryDialog(false); setSummaryDismissed(true); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Some learners were not imported
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You've processed all matched learners (<strong>{markedLearners.size}</strong>),
            but <strong>{unmatchedLearners.length}</strong> learner{unmatchedLearners.length === 1 ? '' : 's'} in
            this Moodle export {unmatchedLearners.length === 1 ? 'was' : 'were'} never linked to a Caliper learner,
            so {unmatchedLearners.length === 1 ? 'their submission was' : 'their submissions were'} <strong>not created</strong>.
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These learners will have no submission to mark. Link them to an existing learner,
            or use <em>Add to Class</em>, then create their submissions.
          </Alert>
          <List dense sx={{ maxHeight: 240, overflow: 'auto', bgcolor: 'background.default', borderRadius: 1 }}>
            {unmatchedLearners.map((l, i) => (
              <ListItem key={i}>
                <ListItemText primary={l.full_name} secondary={l.email} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSummaryDialog(false);
              setSummaryDismissed(true);
              setFilterStatus('unmatched');
            }}
            variant="contained"
            color="warning"
          >
            Review unmatched
          </Button>
          <Button onClick={() => { setShowSummaryDialog(false); setSummaryDismissed(true); }}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Run Results */}
      <Dialog open={showBulkResultDialog} onClose={() => setShowBulkResultDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk submission results</DialogTitle>
        <DialogContent>
          {(() => {
            const ok = bulkResults.filter(r => r.ok).length;
            const failed = bulkResults.length - ok;
            return (
              <Alert severity={failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {ok} succeeded{failed > 0 ? `, ${failed} failed` : ''} of {bulkResults.length}.
                {failed > 0 ? ' Failed learners were not submitted — review and retry.' : ''}
              </Alert>
            );
          })()}
          <List dense sx={{ maxHeight: 320, overflow: 'auto', bgcolor: 'background.default', borderRadius: 1 }}>
            {bulkResults.map((r, i) => (
              <ListItem key={i}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {r.ok
                        ? <CheckCircleIcon color="success" fontSize="small" />
                        : <WarningIcon color="error" fontSize="small" />}
                      {r.name}
                    </Box>
                  }
                  secondary={r.detail}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkResultDialog(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MoodleImportContent;
